import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getUserSubscription } from '../services/stripeService';

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  console.log('🔍 SubscriptionSuccess Debug:', {
    hasUser: !!user,
    userId: user?.id,
    hasSessionId: !!sessionId,
    sessionId,
    currentURL: window.location.href,
    loading
  });

  useEffect(() => {
    if (user && sessionId) {
      // User is logged in, check subscription status
      const checkSubscription = async () => {
        try {
          // Wait 3 seconds for webhook processing
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          const subscriptionData = await getUserSubscription(user.id);
          setSubscription(subscriptionData);
        } catch (err) {
          console.error('Error checking subscription:', err);
          setError('There was an issue verifying your subscription. Please contact support if you continue to have problems.');
        } finally {
          setLoading(false);
        }
      };

      checkSubscription();
    } else if (!sessionId) {
      // No session ID provided
      setError('Invalid session. Please try subscribing again.');
      setLoading(false);
    } else if (sessionId && !user) {
      // Has session ID but user not logged in - payment succeeded but need to login
      setLoading(false);
    }
  }, [user, sessionId]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleManageSubscription = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">
            Verifying your subscription...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we confirm your payment.
          </Typography>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box textAlign="center">
            <Button variant="contained" onClick={() => navigate('/pricing')}>
              Back to Pricing
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  // Special case: Payment succeeded but user not logged in
  if (sessionId && !user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 80,
                    color: 'success.main',
                    mb: 2
                  }}
                />
              </motion.div>

              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Payment Successful! 🎉
              </Typography>

              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Your premium subscription has been activated
              </Typography>

              <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Next Step: Sign in to access your premium features</strong>
                </Typography>
                <Typography variant="body2">
                  Your payment was processed successfully and your premium subscription is now active. 
                  Please sign in to your account to start using all the premium features.
                </Typography>
              </Alert>

              <Card
                sx={{
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  mb: 4,
                  p: 3
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                  <StarIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Premium Features Now Available
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1} textAlign="left">
                  <Typography variant="body2">✅ Unlimited goals and tracking</Typography>
                  <Typography variant="body2">✅ Advanced talent tree system</Typography>
                  <Typography variant="body2">✅ Detailed analytics and insights</Typography>
                  <Typography variant="body2">✅ Priority support</Typography>
                  <Typography variant="body2">✅ Export your data</Typography>
                </Box>
              </Card>

              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login?redirect=/subscription/success' + (sessionId ? `?session_id=${sessionId}` : ''))}
                  sx={{
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                    }
                  }}
                >
                  Sign In to Continue
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/register')}
                >
                  Create Account
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                You'll receive a confirmation email shortly. Your subscription is already active!
              </Typography>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2
                }}
              />
            </motion.div>

            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              Welcome to Premium! 🎉
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Your subscription has been activated successfully
            </Typography>

            <Card
              sx={{
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                mb: 4,
                p: 3
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <StarIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Premium Features Unlocked
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={1} textAlign="left">
                <Typography variant="body1">✅ Unlimited goals and tracking</Typography>
                <Typography variant="body1">✅ Advanced talent tree system</Typography>
                <Typography variant="body1">✅ Detailed analytics and insights</Typography>
                <Typography variant="body1">✅ Priority support</Typography>
                <Typography variant="body1">✅ Export your data</Typography>
              </Box>
            </Card>

            {subscription && (
              <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Subscription Details:</strong><br/>
                  Plan: {subscription.subscription_plan === 'monthly' ? 'Premium Monthly' : 'Premium Annual'}<br/>
                  Status: {subscription.subscription_status}<br/>
                  {subscription.subscription_expires_at && (
                    <>Next billing: {new Date(subscription.subscription_expires_at).toLocaleDateString()}</>
                  )}
                </Typography>
              </Alert>
            )}

            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                size="large"
                startIcon={<ArrowForwardIcon />}
                onClick={handleContinue}
                sx={{
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                  }
                }}
              >
                Start Using Premium Features
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={handleManageSubscription}
              >
                Manage Subscription
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              You'll receive a confirmation email shortly. If you have any questions, 
              our support team is here to help!
            </Typography>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SubscriptionSuccess; 